import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, photoUrl, errText } from "../lib/api";
import { CATEGORIES, PROVINCES } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { LostFoundFields, LF_EMPTY } from "../components/LostFoundFields";
import { CityInput } from "../components/CityInput";
import { catLabel } from "../lib/i18n";
import { AiDescriptionModal } from "../components/AiDescriptionModal";
import { Button } from "../components/ui/button";
import { IsoAlertFields } from "../components/IsoAlertFields";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { BilingualToggle } from "../components/BilingualToggle";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Sparkles, Upload, X, Loader2, ImagePlus, MapPin, Shield, CheckCircle2 } from "lucide-react";

const PRICE_TYPES = [
  { id: "fixed", key: "post_fixed" },
  { id: "free", key: "post_free" },
  { id: "contact", key: "post_contact" },
];

const MAX_PHOTOS = 10;

export default function CreateListing() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [sp] = useSearchParams();
  const initCat = CATEGORIES.some((c) => c.id === sp.get("category") && c.id !== "all") ? sp.get("category") : "dogs";
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({
    title: "", description: "", category: initCat, lf_type: ["lost", "found"].includes(sp.get("lf")) ? sp.get("lf") : "", species: "", breed: "", age: "",
    price: "", price_type: "fixed", province: "ON", city: "",
    show_email: false, show_phone: false, contact_phone: user?.phone || "", iso_alert: true, iso_range: "100", bilingual: false, ...LF_EMPTY,
  });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staffEdit, setStaffEdit] = useState(false);
  const [status, setStatus] = useState(null);
  const staffPending = staffEdit && status === "pending";
  const [aiOpen, setAiOpen] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [locSource, setLocSource] = useState(null);
  useEffect(() => {
    if (editId) return;
    api.get("/my/default-location").then(({ data }) => {
      if (!data.city && !data.province) return;
      setForm((f) => ({ ...f, city: f.city || data.city, province: data.province || f.province }));
      setLocSource(data.source);
    }).catch(() => {});
  }, [editId]);

  useEffect(() => {
    if (!editId) return;
    api.get(`/listings/${editId}`).then(({ data }) => {
      setForm({
        title: data.title || "", description: data.description || "", category: data.category, lf_type: data.lf_type || "",
        species: data.species || "", breed: data.breed || "", age: data.age || "",
        price: data.price ?? "", price_type: data.price_type, province: data.province, city: data.city,
        show_email: data.show_email, show_phone: data.show_phone, contact_phone: data.contact_phone || "",
        iso_alert: data.iso_alert !== false, iso_range: data.iso_range || "100", bilingual: !!data.bilingual,
        ...Object.fromEntries(Object.keys(LF_EMPTY).map((k) => [k, data[k] || ""])),
      });
      setPhotos(data.photos || []);
      setStaffEdit(!!user && data.user_id !== user.user_id && ["admin", "moderator"].includes(user.role));
      setStatus(data.status);
    });
  }, [editId]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      return toast.error(`You can upload up to ${MAX_PHOTOS} photos per ad.`);
    }
    let toUpload = files;
    if (files.length > remaining) {
      toUpload = files.slice(0, remaining);
      toast.error(`Only ${remaining} more photo${remaining === 1 ? "" : "s"} allowed (max ${MAX_PHOTOS} per ad).`);
    }
    setUploading(true);
    try {
      for (const file of toUpload) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, data.path]);
      }
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (path) => setPhotos((p) => p.filter((x) => x !== path));

  const submit = async (e, approve = false) => {
    e.preventDefault();
    if (!form.title || !form.city) return toast.error(t("post_need_title_city"));
    if (form.category === "lost-found" && !form.lf_type) return toast.error(t("lf_need_type"));
    if (form.category === "lost-found" && (!form.lf_date || !form.lf_last_seen.trim())) return toast.error(t("lfd_need"));
    setSaving(true);
    try {
      const payload = {
        ...form,
        ...(form.category === "lost-found" ? { price_type: "free" } : form.category === "iso" ? { price_type: "contact" } : {}),
        price: !["lost-found", "iso"].includes(form.category) && form.price_type === "fixed" ? parseFloat(form.price) || 0 : null,
        photos,
        contact_email: user?.email || "",
      };
      const { data } = editId
        ? await api.put(`/listings/${editId}`, payload)
        : await api.post("/listings", payload);
      if (approve) {
        await api.post(`/staff/listings/${editId}/approve`);
        toast.success(t("post_saved_approved"));
        return navigate(`/listing/${data.id}`);
      }
      toast.success(editId ? t("post_updated") : t("post_submitted"));
      navigate(`/listing/${data.id}`, editId ? undefined : { state: { justPosted: true } });
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const isPet = ["dogs", "cats", "birds", "reptiles", "aquatics", "small-pets", "livestock", "rescue", "lost-found", "iso"].includes(form.category);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display text-3xl font-extrabold text-[#12234A]">{editId ? t("post_edit_title") : t("post_title")}</h1>
      <p className="text-slate-500 mt-1">{t("post_sub")}</p>

      <div className="mt-4 flex items-center gap-2 rounded-xl bg-[#FFF4F0] border border-[#F5551E]/20 px-4 py-3" data-testid="canada-disclaimer">
        <MapPin size={16} className="text-[#F5551E] shrink-0" />
        <p className="text-sm font-medium text-[#12234A]">{t("post_canada")}</p>
      </div>

      <form onSubmit={submit} className="mt-8 space-y-6">
        {staffEdit && (
          <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 text-blue-800 text-sm rounded-xl px-4 py-3" data-testid="staff-editing-notice">
            <Shield size={16} className="mt-0.5 shrink-0" /> <span>{t("post_staff_editing")}</span>
          </div>
        )}
        {/* Photos */}
        <section className="bg-white rounded-2xl border border-slate-200/80 p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-[#12234A]">{t("post_photos")}</h2>
            <span className="text-xs font-semibold text-slate-400" data-testid="photo-count">{photos.length}/{MAX_PHOTOS}</span>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {photos.map((p, i) => (
              <div key={p} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 group">
                <img src={photoUrl(p)} alt="" className="w-full h-full object-cover" />
                {i === 0 && <span className="absolute top-1 left-1 bg-[#F5551E] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">COVER</span>}
                <button type="button" onClick={() => removePhoto(p)} data-testid={`remove-photo-${i}`} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X size={14} /></button>
              </div>
            ))}
            {photos.length < MAX_PHOTOS && (
              <label data-testid="photo-upload-label" className="aspect-square rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-[#F5551E] transition-colors text-slate-400 hover:text-[#F5551E]">
                {uploading ? <Loader2 className="animate-spin" size={22} /> : <ImagePlus size={22} />}
                <span className="text-xs font-medium">{t("post_add_photos")}</span>
                <input type="file" accept="image/*" multiple hidden onChange={onFiles} data-testid="photo-upload-input" />
              </label>
            )}
          </div>
          {photos.length >= MAX_PHOTOS && <p className="text-xs text-slate-400 mt-2" data-testid="photo-max-note">{t("post_max_photos")}</p>}
        </section>

        {/* Details */}
        <section className="bg-white rounded-2xl border border-slate-200/80 p-6 space-y-4">
          <h2 className="font-display font-bold text-[#12234A]">{t("post_details")}</h2>
          <div>
            <Label>{t("post_category")}</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="listing-category" className="mt-1 h-11 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {CATEGORIES.filter((c) => c.id !== "all").map((c) => <SelectItem key={c.id} value={c.id}>{catLabel(lang, c.id, c.label)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.category === "iso" && (
            <>
            <p className="text-sm bg-[#FFF6DD] border border-[#FFC233]/60 text-[#12234A] rounded-xl px-4 py-3" data-testid="iso-hint">
              {lang === "fr" ? "Annonce « Recherché » : décrivez l'animal ou l'article que vous cherchez. Aucun prix n'est affiché et les photos sont facultatives. Un badge ISO apparaîtra sur votre annonce." : "In Search Of ad: describe the pet or item you're looking for. No price is shown and photos are optional. Your ad gets a bold ISO badge."}
            </p>
            <IsoAlertFields on={form.iso_alert} range={form.iso_range} fr={lang === "fr"} onChange={(v) => setForm((f) => ({ ...f, ...v }))} />
            </>
          )}
          {form.category === "lost-found" && (
            <div data-testid="lf-type-picker">
              <Label>{t("lf_type_label")}</Label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {["lost", "found"].map((k) => (
                  <button type="button" key={k} onClick={() => set("lf_type", k)} data-testid={`lf-type-${k}`}
                    className={`h-11 rounded-xl border-2 font-extrabold tracking-wider text-sm transition-colors ${form.lf_type === k ? (k === "lost" ? "bg-red-600 border-red-600 text-white" : "bg-emerald-600 border-emerald-600 text-white") : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                    {t(k === "lost" ? "lf_lost" : "lf_found")}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <Label>{t("post_title_label")}</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="listing-title-input" className="mt-1 h-11 rounded-xl" placeholder={t("post_title_ph")} required />
          </div>
          {isPet && (
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label>{t("post_species")}</Label><Input value={form.species} onChange={(e) => set("species", e.target.value)} data-testid="listing-species" className="mt-1 h-11 rounded-xl" placeholder="Dog" /></div>
              <div><Label>{t("post_breed")}</Label><Input value={form.breed} onChange={(e) => set("breed", e.target.value)} data-testid="listing-breed" className="mt-1 h-11 rounded-xl" placeholder="Golden Retriever" /></div>
              <div><Label>{t("post_age")}</Label><Input value={form.age} onChange={(e) => set("age", e.target.value)} data-testid="listing-age" className="mt-1 h-11 rounded-xl" placeholder="8 weeks" /></div>
            </div>
          )}
          {form.category === "lost-found" && <LostFoundFields form={form} set={set} />}
          <div>
            <div className="flex items-center justify-between">
              <Label>{t("post_description")}</Label>
              <button type="button" onClick={() => setAiOpen(true)} data-testid="open-ai-btn" className="text-sm font-semibold text-[#F5551E] flex items-center gap-1 hover:gap-1.5 transition-all">
                <Sparkles size={15} /> {t("post_generate_ai")}
              </button>
            </div>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="listing-description-input" className="mt-1 rounded-xl" rows={5} placeholder={t("post_desc_ph")} />
          </div>
          <BilingualToggle form={form} set={set} lang={lang} />
        </section>

        {/* Price & location */}
        <section className="bg-white rounded-2xl border border-slate-200/80 p-6 space-y-4">
          <h2 className="font-display font-bold text-[#12234A]">{t("post_price_location")}</h2>
          {form.category !== "lost-found" && form.category !== "iso" && <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>{t("post_price_type")}</Label>
              <Select value={form.price_type} onValueChange={(v) => set("price_type", v)}>
                <SelectTrigger data-testid="listing-price-type" className="mt-1 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {PRICE_TYPES.map((p) => <SelectItem key={p.id} value={p.id}>{t(p.key)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.price_type === "fixed" && (
              <div>
                <Label>{t("post_price_cad")}</Label>
                <Input value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="listing-price-input" type="number" className="mt-1 h-11 rounded-xl" placeholder="0.00" />
              </div>
            )}
          </div>}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>{t("post_province")}</Label>
              <Select value={form.province} onValueChange={(v) => set("province", v)}>
                <SelectTrigger data-testid="listing-province" className="mt-1 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {PROVINCES.filter((p) => p.code !== "all").map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t("post_city")}</Label><div className="mt-1"><CityInput value={form.city} onChange={(v) => set("city", v)} province={form.province} onPick={(c) => setForm((f) => ({ ...f, city: c.city, province: c.province }))} testid="listing-city" className="h-11 rounded-xl" placeholder="Toronto" required /></div></div>
          </div>
          {locSource && <p className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="listing-location-hint"><MapPin size={12} className="text-orange-500" />{t(locSource === "profile" ? "loc_from_profile" : "loc_from_last")}</p>}
        </section>

        {/* Contact */}
        <section className="bg-white rounded-2xl border border-slate-200/80 p-6 space-y-4">
          <h2 className="font-display font-bold text-[#12234A]">{t("post_contact_prefs")}</h2>
          <p className="text-sm text-slate-500">{t("post_contact_sub")}</p>
          <div className="flex items-center justify-between py-2">
            <div><p className="font-medium text-[#12234A]">{t("post_show_email")}</p><p className="text-xs text-slate-400">{staffEdit ? "" : user?.email}</p></div>
            <Switch checked={form.show_email} onCheckedChange={(v) => set("show_email", v)} data-testid="toggle-show-email" />
          </div>
          <div className="flex items-center justify-between py-2 border-t border-slate-100">
            <div className="flex-1"><p className="font-medium text-[#12234A]">{t("post_show_phone")}</p></div>
            <div className="flex items-center gap-3">
              {form.show_phone && <Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="listing-phone" className="h-9 w-40 rounded-lg" placeholder="(416) 555-0000" />}
              <Switch checked={form.show_phone} onCheckedChange={(v) => set("show_phone", v)} data-testid="toggle-show-phone" />
            </div>
          </div>
        </section>

        <div className={staffPending ? "grid sm:grid-cols-2 gap-3" : ""}>
          <Button type="submit" disabled={saving} data-testid="submit-listing-btn" className={`w-full h-12 rounded-xl font-bold text-base ${staffPending ? "bg-white border border-slate-300 text-[#12234A] hover:bg-slate-50" : "bg-[#F5551E] hover:bg-[#E04812] text-white"}`}>
            {saving ? <Loader2 className="animate-spin" size={20} /> : staffEdit ? t("post_save_changes") : t("post_submit")}
          </Button>
          {staffPending && (
            <Button type="button" disabled={saving} onClick={(e) => submit(e, true)} data-testid="save-approve-btn" className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base gap-2">
              {saving ? <Loader2 className="animate-spin" size={20} /> : <><CheckCircle2 size={18} /> {t("post_save_approve")}</>}
            </Button>
          )}
        </div>
      </form>

      <AiDescriptionModal
        open={aiOpen}
        onOpenChange={setAiOpen}
        seed={{ title: form.title, category: form.category, species: form.species, breed: form.breed, age: form.age }}
        onApply={(text) => set("description", text)}
      />
    </div>
  );
}
